import {
    createFeatureSelector,
    createSelector
} from '@ngrx/store';
import {
    DATIQ_FEATURE_FLAG_FEATURE_KEY,
    datiqFeatureFlagAdapter,
    DatiqFeatureFlagPartialState,
    IDatiqFeatureFlagEntity,
    initialState,
    State
} from './datiq-feature-flag.models';

// Lookup the 'DatiqFeatureFlag' feature state managed by NgRx
export const getDatiqFeatureFlagState = createFeatureSelector<
    DatiqFeatureFlagPartialState,
    State
>(DATIQ_FEATURE_FLAG_FEATURE_KEY);

const { selectAll, selectEntities } = datiqFeatureFlagAdapter.getSelectors();

export const getDatiqFeatureFlagLoaded = () =>
    createSelector(getDatiqFeatureFlagState, (state: State) => state?.loaded);

export const getDatiqFeatureFlagError = () =>
    createSelector(getDatiqFeatureFlagState, (state: State) => state?.error);

export const getAllDatiqFeatureFlag = () =>
    createSelector(getDatiqFeatureFlagState, (state: State) =>
        selectAll(state)
    );

export const getDatiqFeatureFlagEntities = () =>
    createSelector(getDatiqFeatureFlagState, (state: State) =>
        selectEntities(state ?? initialState)
    );
export const useFlags = () =>
    createSelector(getDatiqFeatureFlagState, (state: State) => state?.useFlags);

export const getSelectedId = () =>
    createSelector(
        getDatiqFeatureFlagState,
        (state: State) => state.selectedId
    );

export const getSelected = () =>
    createSelector(
        getDatiqFeatureFlagEntities(),
        getSelectedId(),
        (entities, selectedId) => selectedId && entities[selectedId]
    );
export const getFlags = (featureFlagKeys: string[]) =>
    createSelector(getDatiqFeatureFlagEntities(), (entities) =>
        featureFlagKeys
            .map(
                (featureFlagKey: string): IDatiqFeatureFlagEntity =>
                    entities[featureFlagKey]
            )
    );
export const getAllEnabledFeatureFlags = () =>
    createSelector(getDatiqFeatureFlagEntities(), (entities) =>
        Object.values(entities).filter(
            (entity: IDatiqFeatureFlagEntity): boolean => entity.enabled
        )
    );
export const getEnabledFlags = (featureFlagKeys: string[]) =>
    createSelector(
        getFlags(featureFlagKeys),
        (featureFlags: IDatiqFeatureFlagEntity[]) =>
            featureFlags.filter(
                (featureFlag: IDatiqFeatureFlagEntity): boolean =>
                    featureFlag?.enabled
            )
    );
export const getEnabledFlag = (featureFlagKey: string) =>
    createSelector(
        getEnabledFlags([featureFlagKey]),
        (featureFlags: IDatiqFeatureFlagEntity[]) => featureFlags[0]
    );
export const hasVariation = (featureFlagKey: string, variation: string) =>
    createSelector(
        getEnabledFlag(featureFlagKey),
        (featureFlag: IDatiqFeatureFlagEntity) => {
            return featureFlag?.variation === variation;
        }
    );
export const areEnabled = (featureFlagKeys: string[]) =>
    createSelector(
        getEnabledFlags(featureFlagKeys),
        useFlags(),
        (
            featureFlags: IDatiqFeatureFlagEntity[],
            useFlags: boolean
        ) => {
            return !!featureFlags?.length;
        }
    );

export const getEnabledVariables = (featureFlagKeys: string[]) =>
    createSelector(
        getEnabledFlags(featureFlagKeys),
        (featureFlags: IDatiqFeatureFlagEntity[]) =>
            featureFlags.reduce(
                (
                    currentVariables: { [key: string]: unknown },
                    featureFlag: IDatiqFeatureFlagEntity
                ): { [key: string]: unknown } => {
                    return {
                        ...currentVariables,
                        [featureFlag.key]: !!featureFlag?.variables
                            ? featureFlag.variables
                            : null,
                    };
                },
                {}
            )
    );
